import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../redux';
import { usePickListOptions } from '../../../../hooks/usePickListOptions';
import { IField, IFlow } from './EvaluationInterface';
import { getSQLDataType, toBoolean } from '../../../CommonFunctions/CommonFunctions';
import { setRightOperand } from '../../../../redux/Calculations/CalculationsActions';
import { useDispatch } from 'react-redux';
import { Checkbox, Skeleton, Stack } from '@mui/material';
import { fontSize, palette } from '../../../..';
interface PicklistProps {
  propKey: string;
  conditionFilterID: string;
  userSelection: any[];
  timeGrain?: string;
  shouldExclude: boolean;
  filterIdx: number;
  fieldDetails: IField | null;
  dataType: string | null;
  calculationDetails: IFlow | null;
  flowId?: string | null;
  disabled?: boolean;
}
const Picklist = React.memo(
  ({
    propKey,
    conditionFilterID,
    filterIdx,
    flowId,
    calculationDetails,
    fieldDetails,
    userSelection,
    shouldExclude,
    dataType,
    timeGrain = 'year',
    disabled,
  }: PicklistProps) => {
    const dispatch = useDispatch();
    const { dsInfo } = useSelector((state: RootState) => ({
      dsInfo: state.chartProperties.properties[propKey]?.selectedDs as {
        connectionId: string;
        id: string;
        datasetName: string;
        isFlatFileDataset: boolean;
        workSpaceId: string;
      },
      tableId: state.chartProperties.properties[propKey]?.selectedTableId,
    }));
    const getOption = (value: string | number | null) => {
      if (value === null) return '(Blank)';
      if (value === '') return '(Empty)';
      return String(value);
    };
    const {
      result: options,
      error,
      loading,
    } = usePickListOptions(
      dsInfo.connectionId,
      dsInfo.id,
      dsInfo.workSpaceId,
      calculationDetails,
      timeGrain,
      flowId ? Object.values(calculationDetails?.fields!)[0].tableId : fieldDetails?.tableId,
      flowId ? calculationDetails?.calculatedFieldName : fieldDetails?.fieldName,
      dataType!,
      !!flowId,
    );

    useEffect(() => {
      if (loading || error) return;
      const validSelections = userSelection.filter((selected) => options.includes(selected));
      const shouldDispatch =
        userSelection.length === 0 || validSelections.length !== userSelection.length;
      if (shouldDispatch) {
        dispatch(setRightOperand(propKey, conditionFilterID, filterIdx, [], []));
      }
    }, [options]);

    const handleCheckboxClick = (e: React.ChangeEvent<HTMLInputElement>, type?: string) => {
      if (disabled) return;
      const option = e.target.name.toString();
      const isChecked = e.target.checked;
      if (option === '(All)') {
        if (isChecked) {
          const resultantDatatype = options.map((option) => getSQLDataType(option));
          dispatch(
            setRightOperand(propKey, conditionFilterID, filterIdx, options, resultantDatatype),
          );
        } else {
          dispatch(setRightOperand(propKey, conditionFilterID, filterIdx, [], []));
        }
      } else {
        if (isChecked) {
          let _name: string | number | null;
          if (option === '(Blank)') {
            _name = null;
          } else if (option === '(Empty)') {
            _name = '';
          } else if (type === 'text') _name = option;
          else {
            const parsedNumber = Number(option);
            _name = isNaN(parsedNumber) ? option : parsedNumber;
          }
          const newSelection = [
            ...userSelection,
            type === 'boolean' ? toBoolean(_name as string) : _name,
          ];
          const resultantDatatype = newSelection.map((option) =>
            getSQLDataType(type === 'boolean' ? 'boolean' : option),
          );
          dispatch(
            setRightOperand(propKey, conditionFilterID, filterIdx, newSelection, resultantDatatype),
          );
        } else {
          const newSelection = userSelection.filter((_option) => {
            const targetName =
              option === '(Blank)'
                ? null
                : option === '(Empty)'
                  ? ''
                  : type === 'text'
                    ? option
                    : !isNaN(Number(option))
                      ? Number(option)
                      : type === 'boolean'
                        ? toBoolean(option)
                        : option;
            return _option !== targetName;
          });
          const resultantDatatype = newSelection.map((_option_) => getSQLDataType(_option_));
          dispatch(
            setRightOperand(propKey, conditionFilterID, filterIdx, newSelection, resultantDatatype),
          );
        }
      }
    };

    if (loading) {
      return (
        <Stack
          spacing={1}
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Skeleton variant="text" sx={{ fontSize: '1rem' }} width="90%" />
          <Skeleton variant="text" sx={{ fontSize: '1rem' }} width="90%" />
          <Skeleton variant="text" sx={{ fontSize: '1rem' }} width="90%" />
        </Stack>
      );
    }
    if (error) {
      return (
        <div
          style={{
            color: '#850000ff',
          }}
        >
          {' '}
          {error.code === 422 ? 'The Column is no longer available' : 'Error: ' + error.message}
        </div>
      );
    }
    return (
      <div>
        <label className="UserFilterCheckboxes" style={{ marginLeft: '2.5px' }}>
          <Checkbox
            disabled={disabled}
            checked={userSelection.length === options.length ? true : false}
            sx={{
              transform: 'scale(0.8)',
              // marginLeft: "10px",
              paddingRight: '0px',
              '&.Mui-checked': {
                color: shouldExclude ? 'orange' : '#a6a6a6',
              },
            }}
            onChange={(e) => handleCheckboxClick(e)}
            name="(All)"
          />

          <span
            title="(All)"
            style={{
              marginLeft: '0.3rem',
              // marginTop: "3.5px",
              justifySelf: 'center',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              fontSize: fontSize.medium,
              color: palette.primary.contrastText,
              textDecoration:
                shouldExclude && userSelection.includes('(All)') ? 'line-through' : 'none',
            }}
          >
            {'(All)'}
          </span>
        </label>
        {options.map((option, index) => (
          <label className="UserFilterCheckboxes" key={index} style={{ marginLeft: '2.5px' }}>
            <Checkbox
              disabled={disabled}
              checked={userSelection.includes(option) ? true : false}
              sx={{
                transform: 'scale(0.8)',
                // marginLeft: "10px",
                paddingRight: '0px',
                '&.Mui-checked': {
                  color: shouldExclude ? 'orange' : '#a6a6a6',
                },
              }}
              onChange={(e) => {
                handleCheckboxClick(e, getSQLDataType(option));
              }}
              name={getOption(option)}
            />

            <span
              title={getOption(option)}
              style={{
                marginLeft: '0.3rem',
                // marginTop: "3.5px",
                justifySelf: 'center',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                fontSize: fontSize.medium,
                color: palette.primary.contrastText,
                textTransform: 'capitalize',
                textDecoration:
                  shouldExclude && userSelection.includes(option) ? 'line-through' : 'none',
              }}
            >
              {getOption(option)}
            </span>
          </label>
        ))}
      </div>
    );
  },
);

export default Picklist;
